import { useState, useEffect } from 'react';
import { inBrowser } from '@fogia/core-web/constants';

const getPrefersReducedMotion = (): boolean => {
    if (!inBrowser) {
        return false;
    }

    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

/**
 * Returns whether the user has requested reduced motion via the OS accessibility setting.
 * Listens for changes so the value updates if the user changes the setting during a session.
 *
 * Use this to disable autoplay, animations, or transitions for users who prefer reduced motion.
 */
const usePrefersReducedMotion = (): boolean => {
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(getPrefersReducedMotion);

    useEffect(() => {
        if (!inBrowser) {
            return undefined;
        }

        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        const handleChange = (query: MediaQueryListEvent | MediaQueryList) => {
            setPrefersReducedMotion(query.matches);
        };

        handleChange(mediaQuery);

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleChange);
            return () => mediaQuery.removeEventListener('change', handleChange);
        }

        mediaQuery.addListener(handleChange);
        return () => mediaQuery.removeListener(handleChange);
    }, []);

    return prefersReducedMotion;
};

export default usePrefersReducedMotion;
